import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Trash2, Shield, Pencil, Loader2} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface ApprovalStep {
  id?: string;
  stepOrder: number;
  approverRole?: string | null;
  approverUserId?: string | null;
  isRequired: boolean;
}
interface ApprovalRule {
  id: string;
  name: string;
  description: string | null;
  entityType: string;
  conditionField: string;
  conditionOperator: string;
  conditionValue: string;
  priority: number;
  isActive: boolean;
  steps: ApprovalStep[];
}

const ENTITY_TYPES = [
  { value: "order", label: "Sales Order" },
  { value: "purchase_order", label: "Purchase Order" },
];
const CONDITION_OPERATORS = [
  { value: "greater_than", label: ">" },
  { value: "greater_than_or_equal", label: ">=" },
  { value: "less_than", label: "<" },
  { value: "less_than_or_equal", label: "<=" },
  { value: "equal", label: "=" },
];
const APPROVER_ROLES = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
];

export default function ApprovalRulesPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "", description: "", entityType: "order",
    conditionField: "total_amount", conditionOperator: "greater_than",
    conditionValue: "", priority: 0, isActive: true,
    steps: [{ stepOrder: 1, approverRole: "admin", isRequired: true } as ApprovalStep],
  });

  const { data: rules = [] } = useQuery<ApprovalRule[]>({
    queryKey: ["/api/approval-rules"],
    queryFn: () => api.get<ApprovalRule[]>(`/approval-rules`, sessionId),
  });

  const saveMutation = useMutation({
    mutationFn: () => editingId
      ? api.patch(`/approval-rules/${editingId}`, form, sessionId)
      : api.post(`/approval-rules`, form, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/approval-rules"] });
      setDialogOpen(false);
      setEditingId(null);
      toast({ title: editingId ? "Rule updated" : "Rule created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/approval-rules/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/approval-rules"] });
      toast({ title: "Rule deleted" });
    },
  });

  const handleNew = () => {
    setEditingId(null);
    setForm({
      name: "", description: "", entityType: "order",
      conditionField: "total_amount", conditionOperator: "greater_than",
      conditionValue: "", priority: 0, isActive: true,
      steps: [{ stepOrder: 1, approverRole: "admin", isRequired: true }],
    });
    setDialogOpen(true);
  };

  const handleEdit = (rule: ApprovalRule) => {
    setEditingId(rule.id);
    setForm({
      name: rule.name,
      description: rule.description || "",
      entityType: rule.entityType,
      conditionField: rule.conditionField,
      conditionOperator: rule.conditionOperator,
      conditionValue: rule.conditionValue,
      priority: rule.priority,
      isActive: rule.isActive,
      steps: rule.steps.length > 0 ? rule.steps : [{ stepOrder: 1, approverRole: "admin", isRequired: true }],
    });
    setDialogOpen(true);
  };

  const addStep = () => setForm((f) => ({
    ...f, steps: [...f.steps, { stepOrder: f.steps.length + 1, approverRole: "admin", isRequired: true }],
  }));
  const removeStep = (idx: number) => setForm((f) => ({
    ...f, steps: f.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stepOrder: i + 1 })),
  }));
  const updateStep = (idx: number, patch: Partial<ApprovalStep>) => setForm((f) => ({
    ...f, steps: f.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
  }));

  const opLabel = (op: string) => CONDITION_OPERATORS.find((o) => o.value === op)?.label || op;
  const entityLabel = (et: string) => ENTITY_TYPES.find((e) => e.value === et)?.label || et;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Approval Rules"
        description="Configure when documents require approval and who needs to approve them"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNew}>
              <Plus className="h-4 w-4 mr-1" /> Add Rule
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-4 w-4" /> Rules ({rules.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rules.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No approval rules configured. Create rules to require approvals for high-value orders and purchase orders.
            </p>
          ) : (
            <div className="space-y-2">
              {rules.map((rule) => (
                <div key={rule.id} className="p-3 rounded-md border">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{rule.name}</span>
                        <Badge variant="outline" className="text-xs">{entityLabel(rule.entityType)}</Badge>
                        {!rule.isActive && <Badge variant="outline" className="text-xs">Inactive</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {rule.conditionField.replace(/_/g, " ")} {opLabel(rule.conditionOperator)} $
                        {parseFloat(rule.conditionValue).toLocaleString()}{" "}· {rule.steps.length} step{rule.steps.length !== 1 ? "s" : ""}
                      </p>
                      {rule.description && (
                        <p className="text-sm text-muted-foreground mt-2">{rule.description}</p>
                      )}
                      <div className="mt-2 space-y-1">
                        {rule.steps.map((step) => (
                          <div key={step.id} className="flex items-center gap-2 text-sm">
                            <Badge variant="outline" className="min-w-[60px] justify-center">Step {step.stepOrder}</Badge>
                            <span>
                              {step.approverRole
                                ? `Role: ${step.approverRole}`
                                : step.approverUserId
                                  ? `User: ${step.approverUserId}`
                                  : "Unassigned"}
                            </span>
                            {step.isRequired && <Badge variant="secondary" className="text-xs">Required</Badge>}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(rule)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => { if (confirm(`Delete rule "${rule.name}"?`)) deleteMutation.mutate(rule.id); }}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Approval Rule" : "Create Approval Rule"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Rule Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Large Order Approval"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="When this rule triggers..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Entity Type</label>
                <Select value={form.entityType} onValueChange={(v) => setForm({ ...form, entityType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((et) => (
                      <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Priority</label>
                <Input
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value, 10) || 0 })}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-sm font-medium mb-1 block">Field</label>
                <Input value={form.conditionField} onChange={(e) => setForm({ ...form, conditionField: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Operator</label>
                <Select value={form.conditionOperator} onValueChange={(v) => setForm({ ...form, conditionOperator: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDITION_OPERATORS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Value ($)</label>
                <Input
                  type="number"
                  value={form.conditionValue}
                  onChange={(e) => setForm({ ...form, conditionValue: e.target.value })}
                  placeholder="10000"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Active
            </label>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Approval Steps</label>
                <Button type="button" variant="outline" size="sm" onClick={addStep}>
                  <Plus className="h-3 w-3 mr-1" /> Add Step
                </Button>
              </div>
              {form.steps.map((step, idx) => (
                <div key={idx} className="flex items-end gap-2 mb-2 p-2 border rounded-md">
                  <div className="flex-1">
                    <label className="text-xs">Step {step.stepOrder}</label>
                    <Select
                      value={step.approverRole ? `role:${step.approverRole}` : (step.approverUserId || "role:admin")}
                      onValueChange={(val) => {
                        if (val.startsWith("role:")) {
                          updateStep(idx, { approverRole: val.replace("role:", ""), approverUserId: null });
                        } else {
                          updateStep(idx, { approverUserId: val, approverRole: null });
                        }
                      }}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {APPROVER_ROLES.map((r) => (
                          <SelectItem key={r.value} value={`role:${r.value}`}>Role: {r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button" variant="ghost" size="icon" className="h-9 w-9"
                    onClick={() => removeStep(idx)} disabled={form.steps.length <= 1}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={!form.name || !form.conditionValue || saveMutation.isPending}
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingId ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
